import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { Clock, Moon, Sun } from 'lucide-react';
import { calculateNightShiftMetrics, formatMinutes } from '@/utils/timeFormat';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateScheduleException } from '@/hooks/schedules/useScheduleExceptions';
import type { CreateScheduleExceptionDto, ScheduleType } from '@/types/schedule.types';

// Employee schedule info for replacement filtering
interface EmployeeWithSchedule {
  id: string;
  name: string;
  position?: string | null;
  isWorkingDay: boolean;
  shiftStart?: string | null;
  shiftEnd?: string | null;
}

interface CreateExceptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  employeeId: string | null;
  date: string | null;
  employeeName?: string;
  // Employees with their schedules for the selected date
  employeesWithSchedule?: EmployeeWithSchedule[];
}

const SCHEDULE_TYPES: { value: ScheduleType; label: string }[] = [
  { value: 'working', label: 'Рабочий день' },
  { value: 'day_off', label: 'Выходной' },
  { value: 'holiday', label: 'Праздник' },
  { value: 'reduced', label: 'Сокращённый день' },
];

export const CreateExceptionDialog: React.FC<CreateExceptionDialogProps> = ({
  open,
  onOpenChange,
  employeeId,
  date,
  employeeName,
  employeesWithSchedule = [],
}) => {
  const [isWorkingDay, setIsWorkingDay] = useState(true);
  const [isReplacement, setIsReplacement] = useState(false);
  const [scheduleType, setScheduleType] = useState<ScheduleType>('working');
  const [replacementEmployeeId, setReplacementEmployeeId] = useState<string | null>(null);

  const createException = useCreateScheduleException();

  const {
    register,
    handleSubmit,
    reset,
    watch,
  } = useForm<{
    shift_start: string;
    shift_end: string;
    reason: string;
    comment: string;
  }>();

  // Watch shift times for real-time calculation
  const shiftStart = watch('shift_start');
  const shiftEnd = watch('shift_end');

  // Calculate night shift metrics in real-time
  const nightMetrics = useMemo(() => {
    if (!isWorkingDay || isReplacement) return null;
    return calculateNightShiftMetrics(shiftStart, shiftEnd);
  }, [shiftStart, shiftEnd, isWorkingDay, isReplacement]);

  // Reset form when dialog opens - use real schedule times if available
  useEffect(() => {
    if (open) {
      // Find current employee's schedule for this date
      const currentEmployee = employeesWithSchedule?.find(
        (emp) => emp.id === employeeId
      );

      // Use real schedule times or fallback to defaults
      const defaultStart = currentEmployee?.shiftStart?.slice(0, 5) || '09:00';
      const defaultEnd = currentEmployee?.shiftEnd?.slice(0, 5) || '18:00';

      reset({
        shift_start: defaultStart,
        shift_end: defaultEnd,
        reason: '',
        comment: '',
      });
      setIsWorkingDay(true);
      setIsReplacement(false);
      setScheduleType('working');
      setReplacementEmployeeId(null);
    }
  }, [open, reset, employeeId, employeesWithSchedule]);

  const onSubmit = async (formData: {
    shift_start: string;
    shift_end: string;
    reason: string;
    comment: string;
  }) => {
    if (!employeeId || !date) return;

    const dto: CreateScheduleExceptionDto = {
      employee_id: employeeId,
      date,
      is_working_day: isWorkingDay,
      schedule_type: scheduleType,
      reason: formData.reason || undefined,
      comment: formData.comment || 'Исключение создано через админ панель',
      is_replacement: isReplacement,
      replacement_employee_id: isReplacement ? replacementEmployeeId || undefined : undefined,
    };

    // Add shift times only for working days
    // Backend expects HH:mm:ss format, but input type="time" gives HH:mm
    if (isWorkingDay && !isReplacement) {
      dto.new_shift_start = formData.shift_start ? `${formData.shift_start}:00` : undefined;
      dto.new_shift_end = formData.shift_end ? `${formData.shift_end}:00` : undefined;
    }

    try {
      await createException.mutateAsync(dto);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create exception:', error);
    }
  };

  // Filter replacement employees:
  // 1. Exclude current employee
  // 2. Only show employees who have a working day on this date
  const availableReplacementEmployees = useMemo(() => {
    return employeesWithSchedule.filter(
      (emp) => emp.id !== employeeId && emp.isWorkingDay
    );
  }, [employeesWithSchedule, employeeId]);

  // Helper to format time for display
  const formatTime = (time: string | null | undefined): string => {
    if (!time) return '';
    // Handle both "HH:mm:ss" and "HH:mm" formats
    return time.slice(0, 5);
  };

  const formattedDate = date
    ? format(new Date(date), 'd MMMM yyyy (EEEE)', { locale: ru })
    : '';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Создать исключение в графике</DialogTitle>
          <DialogDescription>
            {employeeName && (
              <span className="font-medium">{employeeName}</span>
            )}
            {date && (
              <span className="block mt-1 text-sm">{formattedDate}</span>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Schedule Type */}
          <div className="space-y-2">
            <Label>Тип дня</Label>
            <Select
              value={scheduleType}
              onValueChange={(value: ScheduleType) => {
                setScheduleType(value);
                // Auto-set is_working_day based on type
                setIsWorkingDay(value === 'working' || value === 'reduced');
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCHEDULE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Is Working Day Toggle */}
          <div className="flex items-center justify-between">
            <Label htmlFor="is-working">Рабочий день</Label>
            <Switch
              id="is-working"
              checked={isWorkingDay}
              onCheckedChange={setIsWorkingDay}
            />
          </div>

          {/* Replacement Toggle */}
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="is-replacement">Замена сотрудника</Label>
              <p className="text-xs text-muted-foreground mt-1">
                Сотрудник получит выходной, время будет взято из графика заменяющего
              </p>
            </div>
            <Switch
              id="is-replacement"
              checked={isReplacement}
              onCheckedChange={(checked) => {
                setIsReplacement(checked);
                if (checked) {
                  // Employee being replaced gets day off
                  setIsWorkingDay(false);
                  setScheduleType('day_off');
                }
              }}
            />
          </div>

          {/* Replacement Employee Selector */}
          {isReplacement && (
            <div className="space-y-2">
              <Label>Заменяющий сотрудник</Label>
              <p className="text-xs text-muted-foreground">
                Показаны только сотрудники с рабочим днём на эту дату
              </p>
              {availableReplacementEmployees.length === 0 ? (
                <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md">
                  Нет доступных сотрудников с рабочим днём на эту дату
                </div>
              ) : (
                <Select
                  value={replacementEmployeeId || ''}
                  onValueChange={(value) => setReplacementEmployeeId(value || null)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Выберите сотрудника" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableReplacementEmployees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        <div className="flex items-center gap-2">
                          <span>{emp.name}</span>
                          {emp.position && (
                            <span className="text-muted-foreground">
                              ({emp.position})
                            </span>
                          )}
                          <span className="flex items-center gap-1 text-xs text-green-600">
                            <Clock className="h-3 w-3" />
                            {formatTime(emp.shiftStart)}-{formatTime(emp.shiftEnd)}
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* Shift Times (only for working days and not replacement) */}
          {isWorkingDay && !isReplacement && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="shift_start">Начало смены</Label>
                  <Input
                    id="shift_start"
                    type="time"
                    {...register('shift_start')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="shift_end">Конец смены</Label>
                  <Input
                    id="shift_end"
                    type="time"
                    {...register('shift_end')}
                  />
                </div>
              </div>

              {/* Night shift info - real-time calculation */}
              {nightMetrics && nightMetrics.totalMinutes > 0 && (
                <div className="flex flex-wrap items-center gap-3 text-sm bg-muted/50 p-3 rounded-md border">
                  {nightMetrics.crossesMidnight && (
                    <span className="flex items-center gap-1.5 text-indigo-600 font-medium">
                      <Moon className="h-4 w-4" />
                      Ночная смена
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Sun className="h-4 w-4 text-amber-500" />
                    Дневных: {formatMinutes(nightMetrics.dayMinutes)}
                  </span>
                  <span className="flex items-center gap-1.5 text-muted-foreground">
                    <Moon className="h-4 w-4 text-indigo-400" />
                    Ночных: {formatMinutes(nightMetrics.nightMinutes)}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason">Причина</Label>
            <Input
              id="reason"
              placeholder="Например: Больничный лист, Отпуск по заявлению"
              {...register('reason')}
            />
          </div>

          {/* Comment */}
          <div className="space-y-2">
            <Label htmlFor="comment">Комментарий</Label>
            <Textarea
              id="comment"
              placeholder="Дополнительная информация..."
              rows={2}
              {...register('comment')}
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Отмена
            </Button>
            <Button
              type="submit"
              disabled={createException.isPending || (isReplacement && !replacementEmployeeId)}
            >
              {createException.isPending ? 'Создание...' : 'Создать'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateExceptionDialog;
